import React, { useState, useEffect } from "react";
import { AuthLogo } from "@/components/Auth/AuthLogo";
import { AuthForm } from "@/components/Auth/AuthForm";
import { NameInput } from "@/components/Auth/NameInput";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { Session } from "@supabase/supabase-js";

const signUpSchema = z.object({
  name: z.string().trim().min(2).max(100),
  email: z.string().trim().email(),
  password: z.string().min(6).max(72),
});

export default function SignUpPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{name?: string; email?: string; password?: string}>({});
  const [isLoading, setIsLoading] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get referral code from URL params
  const referralCode = searchParams.get("ref");

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        if (session?.user) {
          routeUser(session.user.id);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        routeUser(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const routeUser = async (userId: string) => {
    await supabase
      .from("profiles")
      .update({ onboarding_completed: false })
      .eq("id", userId);

    navigate("/onboarding");
  };

  const createReferralRecord = async (newUserId: string) => {
    if (!referralCode) return;
    
    try {
      // Look up the referrer by their invite_code using secure RPC
      const { data: referrerId, error: lookupError } = await supabase
        .rpc('validate_invite_code', { code: referralCode });
      
      if (lookupError || !referrerId) {
        console.log("Referral code not found:", referralCode);
        return;
      }
      
      // Don't allow self-referrals
      if (referrerId === newUserId) {
        console.log("Self-referral attempt blocked");
        return;
      }
      
      // Create the referral record
      const { error: insertError } = await supabase
        .from("referrals")
        .insert({
          referrer_id: referrerId,
          referred_id: newUserId,
          status: "pending"
        });
      
      if (insertError) {
        // Likely a duplicate - referred_id is unique
        console.log("Could not create referral record:", insertError.message);
        return;
      }
      
      console.log("Referral record created successfully");
    } catch (err) {
      console.error("Error creating referral:", err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = signUpSchema.safeParse({ name, email, password });
    if (!result.success) {
      const errs = result.error.flatten().fieldErrors;
      setErrors({
        name: errs.name?.[0],
        email: errs.email?.[0],
        password: errs.password?.[0],
      });
      return;
    }

    setIsLoading(true);
    const redirectUrl = `${window.location.origin}/`;
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: { full_name: name.trim() },
        emailRedirectTo: redirectUrl
      },
    });

    if (error) {
      toast.error(error.message);
      setIsLoading(false);
      return;
    }

    if (data?.user) {
      // Create referral record if there's a referral code
      await createReferralRecord(data.user.id);
      
      // Send welcome email via Brevo (fire-and-forget)
      supabase.functions.invoke("send-email", {
        body: {
          event_type: "welcome",
          recipient_email: email.trim(),
          recipient_name: name.trim(),
        },
      }).catch((err) => console.error("Welcome email error:", err));
      
      toast.success("Account created! Continue onboarding");
      routeUser(data.user.id);
    }

    setIsLoading(false);
  };

  return (
    <main className="flex-grow px-6 flex flex-col items-center justify-center min-h-screen bg-background">
      <div className="w-full max-w-md space-y-6">
        <div className="flex justify-center">
          <AuthLogo />
        </div>

        <section className="flex flex-col items-center gap-2">
          <h1 className="text-2xl font-semibold">Create an account</h1>
          <p className="text-muted-foreground text-center">
            Let's get your learning journey started.
          </p>

          <AuthForm
            view="signup"
            email={email}
            setEmail={setEmail}
            emailError={errors.email}
            password={password}
            setPassword={setPassword}
            passwordError={errors.password}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            extraFields={<NameInput name={name} setName={setName} error={errors.name} />}
          />

          <div className="mt-4 text-muted-foreground text-sm">
            Already have an account?{" "}
            <Link to="/auth/signin" className="text-foreground underline">
              Sign in
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}